import { ApplicationRef, inject, Injectable } from "@angular/core";
import { createEffect, EffectNotification, OnRunEffects } from "@ngrx/effects";
import { catchError, concat, EMPTY, filter, first, from, interval, Observable, of, Subject, switchMap, tap, timeout } from "rxjs";
import { SwUpdate } from "@angular/service-worker";
import { SW_UPDATE_CHECK_INTERVAL } from "./injection-tokens";
import { SwUpdateImmediately } from "./update-strategy/SwUpdateImmediately";
import { SwUpdateStrategy } from "./update-strategy/SwUpdateStrategy";
import { PCP_LOGGER } from "@pcp/logging";

@Injectable()
export class ServiceWorkerEffects implements OnRunEffects {

    ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
        if (!this.ngSwUpdate.isEnabled) {
            return EMPTY;
        }

        return resolvedEffects$;
    }

    private readonly log = inject(PCP_LOGGER).createScoped('service-worker');
    private readonly ngSwUpdate = inject(SwUpdate);

    /**
     * Update the service worker either
     * - immediately (SwUpdateImmediately)
     * - after accepting a confirmation dialog (SwUpdateWithNotification)
     */
    private readonly swUpdater: SwUpdateStrategy = inject(SwUpdateImmediately);
    // private readonly swUpdater: SwUpdateStrategy = inject(SwUpdateWithNotification);

    private readonly appIsStable$ = inject(ApplicationRef).isStable.pipe(
        first((isStable) => isStable),
        timeout({ first: 30_000, with: () => of(true) }),
    );
    private readonly everySixHours$ = interval(inject(SW_UPDATE_CHECK_INTERVAL));
    private readonly everySixHoursOnceAppIsStable$ = concat(this.appIsStable$, this.everySixHours$);

    private readonly newVersionAvailable$ = new Subject<boolean>();

    readonly detectUpdates$ = createEffect(() => this.ngSwUpdate.versionUpdates.pipe(
        tap(evt => {
            this.log.info('version update', { type: evt.type });
            if (evt.type === 'VERSION_READY') {
                this.newVersionAvailable$.next(true);
            }
        }),
    ), { dispatch: false });

    readonly checkForUpdates$ = createEffect(() => this.everySixHoursOnceAppIsStable$.pipe(
        tap(() => this.log.debug('checking for updates')),
        switchMap(() => from(this.ngSwUpdate.checkForUpdate()).pipe(
            catchError((err) => {
                this.log.error("error while checking for updates", err instanceof Error ? err : undefined, { err });
                return EMPTY;
            }),
        )),
    ), { dispatch: false });

    readonly showUpdatePrompt$ = createEffect(() => this.newVersionAvailable$.pipe(
        filter(isNewVersionAvailable => isNewVersionAvailable),
        switchMap(() => this.swUpdater.runUpdate()),
    ), { dispatch: false });

    /**
     * Handle unrecoverable service worker state.
     * This happens when cached files are no longer available on the server (e.g. after deployment)
     * and the SW can't serve or re-fetch them. Without this handler the app is stuck on a broken
     * SW until the user manually clears site data.
     *
     * Fix: unregister the broken SW first, then reload so the browser fetches fresh from the network.
     */
    readonly handleUnrecoverable$ = createEffect(() =>
        this.ngSwUpdate.unrecoverable.pipe(
            tap(event => {
                this.log.critical('Unrecoverable state', undefined, { reason: event.reason });
                navigator.serviceWorker.getRegistration().then(reg => {
                    if (reg) {
                        reg.unregister().then(() => document.location.reload());
                    } else {
                        document.location.reload();
                    }
                });
            }),
        ), { dispatch: false });

}
